import { useEffect } from 'react';
import { CalendarCheck, Mail, MessageSquare, RefreshCw, X } from 'lucide-react';
import { useLead, useNotify, useResendCrm, useResendEvent, useSendEmail } from '../hooks/useLeads';
import {
  BRAND_LABELS,
  CHANNEL_LABELS,
  EVENT_SKIP_REASON_LABELS,
  formatDateTime,
  NOTIFICATION_STATUS_CLASSES,
  NOTIFICATION_STATUS_LABELS,
  SKIP_REASON_LABELS,
  STATUS_CLASSES,
  STATUS_LABELS,
} from '../lib/format';
import type { LeadDetailDto } from '@shared/types';

const SOURCE_LABELS: Record<LeadDetailDto['source'], string> = {
  webhook: 'Webhook',
  backfill: 'Doplněno zpětně',
};

interface Props {
  id: string;
  onClose: () => void;
}

export default function LeadDetail({ id, onClose }: Props) {
  const { data: lead, isLoading, isError } = useLead(id);

  // Escape zavírá drawer stejně jako klik na pozadí.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-30 flex justify-end bg-dark/40" onClick={onClose}>
      <div
        className="h-full w-full max-w-2xl overflow-y-auto bg-white shadow-lift"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading ? (
          <div className="p-8 text-sm text-gray-500">Načítám…</div>
        ) : isError || !lead ? (
          <div className="p-8">
            <button className="btn-ghost mb-4" onClick={onClose}>
              <X size={16} /> Zavřít
            </button>
            <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
              Lead se nepodařilo načíst.
            </div>
          </div>
        ) : (
          <LeadDetailBody lead={lead} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

/** ID tasku z odpovědi CRM — reálná odpověď je `{"message":"…","id":11161}`. */
function crmTaskId(responseJson: string | null): number | null {
  if (!responseJson) return null;
  try {
    const v = (JSON.parse(responseJson) as { id?: unknown }).id;
    return typeof v === 'number' ? v : null;
  } catch {
    return null;
  }
}

function LeadDetailBody({ lead, onClose }: { lead: LeadDetailDto; onClose: () => void }) {
  const resend = useResendCrm(lead.id);
  const resendEvent = useResendEvent(lead.id);
  const sendEmail = useSendEmail(lead.id);
  const notify = useNotify(lead.id);
  const busy = resend.isPending || resendEvent.isPending || sendEmail.isPending || notify.isPending;

  const fields = Object.entries(lead.fields ?? {});

  return (
    <div className="flex min-h-full flex-col">
      {/* (a) hlavička */}
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 px-6 py-4 backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="truncate font-heading text-2xl font-bold text-dark">
              {lead.name || 'Bez jména'}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
              {/* Čas vyplnění v Metě; čas přijetí do aplikace je v sekci Kampaň. */}
              <span>{formatDateTime(lead.metaCreatedTime ?? lead.createdAt)}</span>
              <span>·</span>
              <span>{SOURCE_LABELS[lead.source]}</span>
              <span>·</span>
              <span>{lead.brandId ? BRAND_LABELS[lead.brandId] : 'Bez značky'}</span>
              <span>·</span>
              <span>{lead.formName || lead.formId}</span>
            </div>
          </div>
          <button className="btn-ghost" onClick={onClose} aria-label="Zavřít">
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="space-y-5 p-6">
        {/* (b) kontakt */}
        <section className="card p-5">
          <h3 className="mb-3 font-heading text-base font-semibold text-dark">Kontakt</h3>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="label">E-mail</dt>
              <dd>
                {lead.email ? (
                  <a className="text-teal hover:underline" href={`mailto:${lead.email}`}>
                    {lead.email}
                  </a>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="label">Telefon</dt>
              <dd>
                {lead.phone ? (
                  <a className="text-teal hover:underline" href={`tel:${lead.phone}`}>
                    {lead.phone}
                  </a>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </dd>
            </div>
          </dl>
        </section>

        {/* (c) odpovědi z formuláře */}
        <section className="card p-5">
          <h3 className="mb-3 font-heading text-base font-semibold text-dark">
            Odpovědi z formuláře
          </h3>
          {fields.length === 0 ? (
            <p className="text-sm text-gray-500">Formulář nevrátil žádná pole.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {fields.map(([key, value]) => (
                  <tr key={key} className="border-t border-gray-100 first:border-t-0">
                    <td className="w-1/3 py-2 pr-4 align-top text-xs uppercase tracking-wide text-gray-500">
                      {key}
                    </td>
                    <td className="py-2 align-top break-words">{value || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* (d) kampaň */}
        <section className="card p-5">
          <h3 className="mb-3 font-heading text-base font-semibold text-dark">Kampaň</h3>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="label">Kampaň</dt>
              <dd className="break-words">
                {lead.campaignName || <span className="text-gray-400">—</span>}
              </dd>
            </div>
            <div>
              <dt className="label">ID reklamy</dt>
              <dd className="font-mono text-xs text-gray-600">
                {lead.adId || <span className="font-body text-gray-400">—</span>}
              </dd>
            </div>
            <div>
              <dt className="label">ID stránky</dt>
              <dd className="font-mono text-xs text-gray-600">{lead.pageId}</dd>
            </div>
            <div>
              <dt className="label">Přijato do aplikace</dt>
              <dd className="text-gray-600">{formatDateTime(lead.createdAt)}</dd>
            </div>
          </dl>
        </section>

        {/* (e) CRM */}
        <section className="card p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="font-heading text-base font-semibold text-dark">CRM</h3>
            {lead.crm && (
              <span className={`badge ${STATUS_CLASSES[lead.crm.status]}`}>
                {STATUS_LABELS[lead.crm.status]}
                {lead.crm.skipReason ? ` — ${SKIP_REASON_LABELS[lead.crm.skipReason]}` : ''}
              </span>
            )}
          </div>

          {!lead.crm ? (
            <p className="text-sm text-gray-500">Doručení do CRM zatím neproběhlo.</p>
          ) : (
            <>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="label">ID v CRM</dt>
                  <dd>
                    {(() => {
                      const taskId = crmTaskId(lead.crm.responseJson);
                      return taskId !== null ? (
                        <a
                          href={`https://crm.buydubai.cz/kanbans/lead/2?task=${taskId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-teal underline-offset-2 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          #{taskId} — otevřít v CRM
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      );
                    })()}
                  </dd>
                </div>
                <div>
                  <dt className="label">Pokusů</dt>
                  <dd>{lead.crm.attempts}/5</dd>
                </div>
                <div>
                  <dt className="label">HTTP status</dt>
                  <dd>{lead.crm.responseStatus ?? <span className="text-gray-400">—</span>}</dd>
                </div>
                <div>
                  <dt className="label">Poslední pokus</dt>
                  <dd className="text-gray-600">{formatDateTime(lead.crm.lastAttemptAt)}</dd>
                </div>
                <div>
                  <dt className="label">Další pokus</dt>
                  <dd className="text-gray-600">{formatDateTime(lead.crm.nextAttemptAt)}</dd>
                </div>
              </dl>

              {lead.crm.detail && (
                <p className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                  {lead.crm.detail}
                </p>
              )}

              {lead.crm.responseJson && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm font-medium text-teal">
                    Odpověď CRM
                  </summary>
                  <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700">
                    {lead.crm.responseJson}
                  </pre>
                </details>
              )}
            </>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              className="btn-secondary"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Odeslat lead znovu do CRM?')) resend.mutate();
              }}
            >
              <RefreshCw size={16} />
              {resend.isPending ? 'Odesílám…' : 'Odeslat znovu do CRM'}
            </button>
          </div>

          {resend.isError && <ErrorBanner error={resend.error} />}
          {resend.isSuccess && !resend.isPending && (
            <SuccessBanner text="Lead byl znovu odeslán do CRM." />
          )}
        </section>

        {/* (f) events */}
        <section className="card p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="font-heading text-base font-semibold text-dark">Events</h3>
            {lead.event && (
              <span className={`badge ${STATUS_CLASSES[lead.event.status]}`}>
                {STATUS_LABELS[lead.event.status]}
                {lead.event.skipReason
                  ? ` — ${EVENT_SKIP_REASON_LABELS[lead.event.skipReason]}`
                  : ''}
              </span>
            )}
          </div>

          {!lead.event ? (
            <p className="text-sm text-gray-500">
              Odeslání do aplikace pro eventy zatím neproběhlo.
            </p>
          ) : (
            <>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                {lead.event.routeLabel && (
                  <div className="col-span-2">
                    <dt className="label">Akce</dt>
                    <dd className="font-medium">{lead.event.routeLabel}</dd>
                  </div>
                )}
                <div>
                  <dt className="label">ID registrace</dt>
                  <dd className="font-mono text-xs text-gray-600">
                    {lead.event.registrationId || <span className="font-body text-gray-400">—</span>}
                    {lead.event.duplicate && (
                      <span className="ml-2 badge bg-gray-100 text-gray-500">duplicita</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="label">Pokusů</dt>
                  <dd>{lead.event.attempts}/5</dd>
                </div>
                <div>
                  <dt className="label">HTTP status</dt>
                  <dd>{lead.event.responseStatus ?? <span className="text-gray-400">—</span>}</dd>
                </div>
                <div>
                  <dt className="label">Poslední pokus</dt>
                  <dd className="text-gray-600">{formatDateTime(lead.event.lastAttemptAt)}</dd>
                </div>
                <div>
                  <dt className="label">Další pokus</dt>
                  <dd className="text-gray-600">{formatDateTime(lead.event.nextAttemptAt)}</dd>
                </div>
              </dl>

              {lead.event.detail && (
                <p className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                  {lead.event.detail}
                </p>
              )}

              {lead.event.responseJson && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm font-medium text-teal">
                    Odpověď Events
                  </summary>
                  <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700">
                    {lead.event.responseJson}
                  </pre>
                </details>
              )}
            </>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              className="btn-secondary"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Odeslat lead do aplikace pro eventy?')) resendEvent.mutate();
              }}
            >
              <CalendarCheck size={16} />
              {resendEvent.isPending ? 'Odesílám…' : 'Odeslat do Events'}
            </button>
            <span className="text-xs text-gray-500">
              Odešle i z formuláře, který má Events vypnuté — musí mít ale nastavený endpoint
              a token akce.
            </span>
          </div>

          {resendEvent.isError && <ErrorBanner error={resendEvent.error} />}
          {resendEvent.isSuccess && !resendEvent.isPending && (
            <SuccessBanner text="Lead byl odeslán do aplikace pro eventy." />
          )}
        </section>

        {/* (g) notifikace */}
        <section className="card p-5">
          <h3 className="mb-3 font-heading text-base font-semibold text-dark">Notifikace</h3>

          {lead.notifications.length === 0 ? (
            <p className="text-sm text-gray-500">Zatím žádné notifikace.</p>
          ) : (
            <ul className="space-y-2">
              {lead.notifications.map((n) => (
                <li key={n.id} className="rounded-lg border border-gray-200 p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-dark">{CHANNEL_LABELS[n.channel]}</span>
                    <span className={`badge ${NOTIFICATION_STATUS_CLASSES[n.status]}`}>
                      {NOTIFICATION_STATUS_LABELS[n.status]}
                    </span>
                    {n.manual && (
                      <span className="badge bg-gray-100 text-gray-500">ručně</span>
                    )}
                    <span className="ml-auto text-xs text-gray-500">
                      {formatDateTime(n.createdAt)}
                    </span>
                  </div>
                  <div className="mt-1 text-gray-600">
                    {n.recipient}
                    {n.subject ? ` · ${n.subject}` : ''}
                  </div>
                  {n.status === 'error' && n.detail && (
                    <div className="mt-1 text-xs text-danger">{n.detail}</div>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              className="btn-secondary"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Odeslat leadovi potvrzovací e-mail?')) sendEmail.mutate();
              }}
            >
              <Mail size={16} />
              {sendEmail.isPending ? 'Odesílám…' : 'Odeslat potvrzovací e-mail'}
            </button>
            <button
              className="btn-secondary"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Odeslat notifikaci makléři?')) {
                  notify.mutate({ channel: 'email_broker' });
                }
              }}
            >
              <MessageSquare size={16} />
              {notify.isPending ? 'Odesílám…' : 'Notifikovat makléře'}
            </button>
          </div>

          {sendEmail.isError && <ErrorBanner error={sendEmail.error} />}
          {sendEmail.isSuccess && !sendEmail.isPending && (
            <SuccessBanner text="Potvrzovací e-mail odeslán." />
          )}
          {notify.isError && <ErrorBanner error={notify.error} />}
          {notify.isSuccess && !notify.isPending && (
            <SuccessBanner text="Notifikace makléři odeslána." />
          )}
        </section>
      </div>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Akce selhala';
  return (
    <div className="mt-3 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
      {message}
    </div>
  );
}

function SuccessBanner({ text }: { text: string }) {
  return (
    <div className="mt-3 rounded-md border border-positive/40 bg-positive-bg p-3 text-sm text-positive">
      {text}
    </div>
  );
}
